import { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../contexts";
import ROLES from "../../../constants/roles";
import { getLandingRoute, USER_BASE_PATH } from "../../../utils/commonFunctions/accessScope";

// Auth pages are reachable by anyone and must never be redirected away from.
const PUBLIC_PATH_PREFIXES = ["/login", "/sign-up", "/forget-password", "/forget-userid"];

const SCOPED_PATH_PREFIXES = ["/admin", "/store-admin"];

// Roles whose account is pinned to a group, and so to one area of the app.
const SCOPED_ROLES: string[] = [ROLES.admin, ROLES.user];

/**
 * Keeps an ADMIN inside the area its account is scoped to.
 *
 * The server is the thing that actually enforces this - every listing endpoint pins
 * group_number/store_number from the token. This guard exists so a user who hand edits
 * the URL, or who refreshes on a page they no longer belong on, gets bounced back to
 * their own landing page instead of staring at an empty grid.
 */
const AccessScopeGuard = (): null => {
	const { user } = useContext(AuthContext);
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		if (!user || SCOPED_ROLES.indexOf(user.role as string) === -1 || !user.group_number) {
			return;
		}

		if (PUBLIC_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))) {
			return;
		}

		const params = new URLSearchParams(location.search);
		const groupParam = params.get("gn");
		const storeParam = params.get("sn");

		// A USER has exactly one page, so anything outside /user is out of bounds.
		if (user.role === ROLES.user) {
			const isUserAreaAllowed =
				location.pathname.startsWith(USER_BASE_PATH) &&
				(groupParam === null || groupParam === String(user.group_number)) &&
				(storeParam === null || storeParam === String(user.store_number));

			if (!isUserAreaAllowed) {
				navigate(getLandingRoute(user), { replace: true });
			}
			return;
		}

		// Super admin pages live outside both scoped areas.
		const isInScopedArea = SCOPED_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
		const isGroupAllowed = groupParam === null || groupParam === String(user.group_number);

		// A user created against a single store never leaves that store's area.
		const isStoreAllowed = user.store_number
			? location.pathname.startsWith("/store-admin") &&
			  (storeParam === null || storeParam === String(user.store_number))
			: true;

		if (isInScopedArea && isGroupAllowed && isStoreAllowed) {
			return;
		}

		navigate(getLandingRoute(user), { replace: true });
	}, [user, location.pathname, location.search, navigate]);

	return null;
};

export default AccessScopeGuard;
